import React, { useRef, useState } from "react";
import { ImageUp, SquarePen, Trash2 } from "lucide-react";

interface ImageUploaderProps {
  label: string;
  image: File | string | null;
  onUpload: (file: File) => void;
  onDelete: () => void;
  inputId: string;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  label,
  image,
  onUpload,
  onDelete,
  inputId,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const dropZoneRef = useRef<HTMLLabelElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onUpload(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onUpload(file);
  };

  return image ? (
    <div className="w-full flex items-center justify-start gap-3 mb-4">
      <div className="flex flex-col gap-1">
        <h3 className="text-[#3F5260] text-sm font-medium">{label}</h3>
        <img
          src={image instanceof File ? URL.createObjectURL(image) : image}
          alt="Uploaded preview"
          className="w-20 h-20 rounded-full object-cover"
        />
      </div>
      <label
        htmlFor={inputId}
        className="cursor-pointer text-sm font-medium text-[#647588]"
      >
        <input
          type="file"
          id={inputId}
          accept="image/*"
          className="hidden"
          onChange={handleChange}
        />
        <SquarePen className="w-5 h-5" />
      </label>
      <button
        type="button"
        onClick={onDelete}
        className="text-sm font-medium text-[#FF6C6C]"
      >
        <Trash2 className="w-5 h-5" />
      </button>
    </div>
  ) : (
    <label
      ref={dropZoneRef}
      htmlFor={inputId}
      className={`w-full mb-4 cursor-pointer rounded-md flex flex-col items-center justify-center bg-gray-100 py-4 ${
        isDragging
          ? "border-2 border-dashed border-emerald-500 bg-emerald-50"
          : ""
      }`}
      onDragEnter={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
    >
      <input
        type="file"
        id={inputId}
        accept="image/*"
        className="hidden"
        onChange={handleChange}
      />
      <div className="w-11 h-11 mb-2 rounded-full bg-emerald-600 flex items-center justify-center">
        <ImageUp className="w-5 h-5 text-white" />
      </div>
      <p className="text-xs text-gray-500 text-center">Click or drag to upload {label.toLowerCase()}</p>
    </label>
  );
};
